import { useEffect, useMemo, useState } from 'react';
import { Eye, FileDown, Search, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  useAdminCandidates,
  useAdminCandidateAction,
  useAdminCandidateGdprExport,
  useAdminCandidateGdprDelete,
} from '../../hooks/useAdminCandidates';
import type { AdminCandidate, AdminCandidateStatus, CandidateFilters } from '../../api/adminCandidates';

const candidateTabs: Array<{ value: AdminCandidateStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'under_review', label: 'Under review' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'banned', label: 'Banned' },
];

export function CandidateOversight() {
  const [activeTab, setActiveTab] = useState<AdminCandidateStatus | 'all'>('under_review');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [auditCandidate, setAuditCandidate] = useState<AdminCandidate | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const filters = useMemo<CandidateFilters>(
    () => ({
      status: activeTab === 'all' ? undefined : activeTab,
      query: debouncedSearch || undefined,
    }),
    [activeTab, debouncedSearch],
  );

  const { data, isLoading, isError, refetch, isRefetching } = useAdminCandidates(filters);
  const candidateAction = useAdminCandidateAction(filters);
  const gdprExportMutation = useAdminCandidateGdprExport();
  const gdprDeleteMutation = useAdminCandidateGdprDelete(filters);

  const candidates = data ?? [];

  const handleAction = async (candidate: AdminCandidate, status: AdminCandidateStatus, note: string) => {
    try {
      await candidateAction.mutateAsync({ id: candidate.id, status, note });
      toast.success(`Candidate marked ${status.replace('_', ' ')}.`);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Unable to update candidate status.');
    }
  };

  const handleExport = async (candidate: AdminCandidate) => {
    try {
      const blob = await gdprExportMutation.mutateAsync(candidate.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `candidate-${candidate.id}-export.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('GDPR export downloaded.', {
        description: 'Share this JSON file securely with the candidate.',
      });
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Unable to export candidate data.');
    }
  };

  const handleDelete = async (candidate: AdminCandidate) => {
    const confirmed = window.confirm(
      `This will delete/scrub ${candidate.name}'s data in line with GDPR. This cannot be easily undone. Continue?`,
    );
    if (!confirmed) return;
    try {
      await gdprDeleteMutation.mutateAsync(candidate.id);
      toast.success('Candidate data deleted.', {
        description: 'GDPR delete has been applied and audit logged.',
      });
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Unable to delete candidate data.');
    }
  };

  return (
    <div className="min-h-screen bg-[#030513] pb-24">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#f7ef8a]">Admin · Candidates</p>
            <h1 className="mt-2 text-3xl font-semibold text-white">Candidate oversight</h1>
            <p className="text-sm text-gray-400">Review flagged profiles, suspend risky accounts, and keep an audit trail.</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              className="border-white/10 bg-white/[0.08] pl-9 text-sm text-white placeholder:text-gray-500"
              placeholder="Search name, email, skill…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as AdminCandidateStatus | 'all')} className="space-y-6">
          <TabsList className="flex flex-wrap gap-2 border border-white/10 bg-white/5 p-2">
            {candidateTabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="text-sm capitalize text-gray-200 data-[state=active]:bg-gold-gradient data-[state=active]:text-black"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeTab} className="space-y-4">
            {isError && (
              <Card className="border-red-500/40 bg-red-500/10">
                <CardContent className="flex items-center justify-between p-6 text-sm text-red-100">
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5" />
                    Unable to load candidate data.
                  </div>
                  <Button variant="outline" className="border-white/20 text-white" onClick={() => refetch()}>
                    Retry
                  </Button>
                </CardContent>
              </Card>
            )}

            {!isError && (isLoading || isRefetching) && (
              <Card className="border-white/10 bg-white/[0.02]">
                <CardContent className="p-8 text-sm text-gray-400">Syncing latest flags…</CardContent>
              </Card>
            )}

            {!isLoading &&
              !isError &&
              candidates.map((candidate) => (
                <CandidateCard
                  key={candidate.id}
                  candidate={candidate}
                  onSuspend={() => handleAction(candidate, 'suspended', 'Manual suspension from admin panel.')}
                  onReinstate={() => handleAction(candidate, 'active', 'Reinstated after admin review.')}
                  onBan={() => handleAction(candidate, 'banned', 'Banned for repeated violations.')}
                  onExport={() => handleExport(candidate)}
                  onDelete={() => handleDelete(candidate)}
                  onViewAudit={() => setAuditCandidate(candidate)}
                />
              ))}

            {!isLoading && !isError && candidates.length === 0 && (
              <Card className="border-white/10 bg-white/[0.02]">
                <CardContent className="p-12 text-center text-sm text-gray-400">
                  Nothing to review here yet. Keep monitoring flags and reports.
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={Boolean(auditCandidate)} onOpenChange={(open) => setAuditCandidate(open ? auditCandidate : null)}>
        <DialogContent className="border-white/10 bg-[#070a1f] text-white sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              Audit trail · {auditCandidate?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 max-h-[60vh] space-y-4 overflow-y-auto pr-2 text-sm text-gray-200">
            {auditCandidate?.auditLog.map((event) => (
              <div key={event.id} className="rounded-md border border-white/5 bg-white/[0.03] p-3">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{new Date(event.createdAt).toLocaleString('en-GB')}</p>
                <p className="mt-1 font-semibold text-white">{event.action.replace('_', ' ')}</p>
                <p className="text-xs text-gray-400">Actor: {event.actor}</p>
                {event.note && <p className="mt-1 text-gray-300">{event.note}</p>}
              </div>
            ))}
            {auditCandidate?.auditLog.length === 0 && <p>No audit events yet.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type CandidateCardProps = {
  candidate: AdminCandidate;
  onSuspend: () => Promise<void>;
  onReinstate: () => Promise<void>;
  onBan: () => Promise<void>;
  onExport: () => Promise<void>;
  onDelete: () => Promise<void>;
  onViewAudit: () => void;
};

function CandidateCard({ candidate, onSuspend, onReinstate, onBan, onExport, onDelete, onViewAudit }: CandidateCardProps) {
  const statusColor: Record<AdminCandidateStatus, string> = {
    active: 'bg-emerald-500/20 text-emerald-200',
    under_review: 'bg-amber-500/20 text-amber-200',
    suspended: 'bg-orange-500/20 text-orange-200',
    banned: 'bg-red-500/20 text-red-200',
  };

  return (
    <Card className="border-white/10 bg-white/[0.03]">
      <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle className="text-xl text-white">{candidate.name}</CardTitle>
          <p className="text-sm text-gray-400">{candidate.headline}</p>
          <p className="text-xs text-gray-500">{candidate.location} · {candidate.experienceYears} yrs experience</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={`${statusColor[candidate.status]} capitalize`}>{candidate.status.replace('_', ' ')}</Badge>
          {candidate.flags > 0 && (
            <Badge className="border-red-500/40 bg-red-500/20 text-red-200">
              {candidate.flags} flag{candidate.flags === 1 ? '' : 's'}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-6 pt-0 text-sm text-gray-200">
        <div className="flex flex-wrap gap-2 text-xs">
          {candidate.skills.map((skill) => (
            <Badge key={skill} variant="outline" className="border-white/10 text-gray-200">
              {skill}
            </Badge>
          ))}
        </div>
        <div className="grid gap-4 text-sm text-gray-300 sm:grid-cols-3">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Profile score</p>
            <p className="text-white">{candidate.profileCompleteness}%</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Last active</p>
            <p>{new Date(candidate.lastActive).toLocaleString('en-GB')}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Joined</p>
            <p>{new Date(candidate.joinedAt).toLocaleDateString('en-GB')}</p>
          </div>
        </div>
	        <div className="flex flex-wrap gap-3">
          {candidate.status !== 'suspended' && candidate.status !== 'banned' && (
            <Button variant="outline" className="border-amber-400/30 text-amber-200" onClick={onSuspend}>
              Suspend
            </Button>
          )}
          {candidate.status === 'suspended' && (
            <Button className="bg-gold-gradient text-black hover:brightness-110" onClick={onReinstate}>
              Reinstate
            </Button>
          )}
                {candidate.status !== 'banned' && (
                  <Button variant="ghost" className="text-red-300 hover:text-red-200" onClick={onBan}>
                    Ban
                  </Button>
                )}
	          <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={onExport}>
	            <FileDown className="mr-2 h-4 w-4" />
	            Export (GDPR)
	          </Button>
	          <Button variant="ghost" className="text-red-400 hover:text-red-200" onClick={onDelete}>
	            <Trash2 className="mr-2 h-4 w-4" />
	            Delete (GDPR)
	          </Button>
	          <Button variant="secondary" className="bg-white/10 text-white hover:bg-white/20" onClick={onViewAudit}>
	            <Eye className="mr-2 h-4 w-4" />
	            View audit
	          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
